// src/App.js
import React, { useEffect, useState } from "react";
import { Admin, Resource, CustomRoutes } from "react-admin";
import { Route, useLocation } from "react-router-dom";
import { authProvider, refreshUserRole, getCurrentRole } from "./firebase/auth";
import { dataProvider } from "./firebase/dataProvider";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase/firebase";

// Dashboards
import DashboardAdmin from "./DashboardAdmin";
import DashboardMD from "./DashboardMD";

// Core Modules
import { DriverList } from "./resources/Drivers/DriverList";
import { DriverEdit } from "./resources/Drivers/DriverEdit";
import { DriverCreate } from "./resources/Drivers/DriverCreate";

import { VehicleList } from "./resources/Vehicles/VehicleList";
import { VehicleEdit } from "./resources/Vehicles/VehicleEdit";
import { VehicleCreate } from "./resources/Vehicles/VehicleCreate";

import { AccountList, AccountCreate, AccountEdit } from "./resources/Accounts";

import {
  TransactionList,
  TransactionCreate,
  TransactionEdit,
} from "./resources/transactions";
import TransactionEntryPage from "./pages/TransactionEntry";

import { LedgerList } from "./resources/Ledger";

import { UpcomingExpensesList } from "./resources/UpcomingExpensesList";
import { UpcomingExpensesCreate } from "./resources/UpcomingExpensesCreate";

import { CompanyList, CompanyCreate, CompanyEdit } from "./resources/Companies";

// Letters Module
import { LetterCreate } from "./resources/Letters/LetterCreate";
import { MDLetterList } from "./resources/Letters/MDLetterList";
import MDLetterSign from "./resources/Letters/MDLetterSign";
import { SignedDocuments } from "./resources/Letters/SignedDocuments";
import { LetterHeadUpload } from "./resources/Letters/LetterHeadUpload";
import { MDSignatureUpload } from "./resources/Letters/MDSignatureUpload";
import { LetterList } from "./resources/Letters/LetterList";

// Pages
import ClientPricingSetup from "./pages/ClientPricingSetup";

import ParkingUpdate from "./pages/ParkingUpdate";
import PricingUpdate from "./pages/PricingUpdate";
import InvoiceGenerator from "./pages/InvoiceGenerator";
import {
  DriverMappingList,
  DriverMappingCreate,
  DriverMappingEdit,
} from "./resources/DriverMappings";
import EarningsImport from "./pages/earningsImport/EarningsImport";
import RideHailingUploadHistory from "./pages/earningsImport/RideHailingUploadHistory";
import RideHailingRangeReport from "./pages/earningsImport/RideHailingRangeReport";
import RideHailingWeekPeriods from "./pages/earningsImport/RideHailingWeekPeriods";
import POSUploadHistory from "./pages/earningsImport/POSUploadHistory";
import RunningExpensesUpload from "./pages/earningsImport/RunningExpensesUpload";
import DailyRideHailingUpload from "./pages/earningsImport/daily/DailyRideHailingUpload";
import DailyBookingPortalUpload from "./pages/earningsImport/bookingPortalDaily/DailyBookingPortalUpload";
import MDDailyIncomeReport from "./pages/driverReports/managementReports/MDDailyIncomeReport";
import {
  POSMappingList,
  POSMappingCreate,
  POSMappingEdit,
} from "./resources/POSMappings";
import DriversStatement from "./pages/DriversStatement";
import DriverPlanManager from "./pages/driverReports/DriverPlanManager";
import DriverAssignmentCostSetup from "./pages/driverReports/DriverAssignmentCostSetup";
import InstallmentAdjustments from "./pages/driverReports/InstallmentAdjustments";
import InstallmentCompletionAlerts from "./pages/driverReports/InstallmentCompletionAlerts";
import DashboardOperation from "./DashboardOperation";
import DashboardDocsLink from "./DashboardDocsLink";
import MyLayout from "./layouts/MyLayout";
import CustomLoginPage from "./layouts/CustomLoginPage";  // ⬅️ Add this
// At the TOP of App.js, add this import:
import OrphanedIncomeManager from './pages/driverReports/OrphanedIncomeManager';

// Public driver document upload portal (no login)
import DriverDocsPortalPublic from "./pages/DriverDocsPortalPublic";

// Admin approvals for employee documents
import EmployeeDocsApprovals from "./pages/EmployeeDocsApprovals";
import EmployeeDocsApprovalDetail from "./pages/EmployeeDocsApprovalDetail";
import DriverDocumentsList from "./pages/docsPortal/DriverDocumentsList";
import DriverDocumentsEdit from "./pages/docsPortal/DriverDocumentsEdit";

import CarIdleReport from "./pages/driverReports/managementReports/CarIdleReport";
import CarSheetCompanyPLExtract from "./pages/driverReports/CarSheetCompanyPLExtract";


export default function App() {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const isPublicDriverDocs = location?.pathname === "/driver-docs";


  /* -----------------------------------------------------
     🔥 FIX 1 — Refresh role on login/logout automatically
  ----------------------------------------------------- */
  useEffect(() => {
    if (isPublicDriverDocs) return;
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setRole(null);
        return;
      }

      const updatedRole = await refreshUserRole();
      setRole(updatedRole);
      localStorage.setItem("role", updatedRole);
    });

    return () => unsub();
  }, [isPublicDriverDocs]);

  /* -----------------------------------------------------
     🔥 FIX 2 — Initial role load on first page open
  ----------------------------------------------------- */
  useEffect(() => {
    if (isPublicDriverDocs) return;
    const loadRole = async () => {
      try {
        const updatedRole = await refreshUserRole();

        if (updatedRole) {
          setRole(updatedRole);
          console.log("✅ Firebase role fetched:", updatedRole);
        } else {
          const cached = getCurrentRole();
          setRole(cached || null);
          console.warn("⚠️ Fallback to cached role:", cached);
        }
      } catch (err) {
        console.error("❌ Error fetching role:", err.message);
      } finally {
        setLoading(false);
      }
    };

    loadRole();
  }, [isPublicDriverDocs]);

  if (isPublicDriverDocs) return <DriverDocsPortalPublic />;

  if (loading) {
    return (
      <div
        style={{
          fontFamily: "sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontSize: 18,
        }}
      >
        🔄 Loading dashboard...
      </div>
    );
  }

  console.log("🎯 Active Role:", role);

  /* -----------------------------------------------------
     Shared Resources
  ----------------------------------------------------- */
  const sharedResources = [
    <Resource
      key="companies"
      name="companies"
      list={CompanyList}
      create={CompanyCreate}
      edit={CompanyEdit}
      options={{ label: "Companies" }}
    />,

    <Resource
      key="drivers"
      name="drivers"
      list={DriverList}
      edit={DriverEdit}
      create={DriverCreate}
      options={{ label: "Employees" }}
    />,

    <Resource
      key="vehicles"
      name="vehicles"
      list={VehicleList}
      edit={VehicleEdit}
      create={VehicleCreate}
      options={{ label: "Vehicles" }}
    />,
<Resource
    key="driversStatement"
    name="driversStatement"
    list={DriversStatement}
    options={{ label: "📄 Drivers Statement" }}
  />,

    <Resource
      key="upcomingExpenses"
      name="upcomingExpenses"
      list={UpcomingExpensesList}
      create={UpcomingExpensesCreate}
      options={{ label: "Upcoming Expenses" }}
    />,
  ];

  /* -----------------------------------------------------
     Admin Resources
  ----------------------------------------------------- */
  const adminResources = [
    <Resource key="ledger" name="ledger" list={LedgerList} />,
    <Resource
      key="carSheetCompanyPLExtract"
      name="carSheetCompanyPLExtract"
      list={CarSheetCompanyPLExtract}
      options={{ label: "Car Sheet P&L Extract" }}
    />,

    <Resource
  key="driverMappings"
  name="driverMappings"
  list={DriverMappingList}
  create={DriverMappingCreate}
  edit={DriverMappingEdit}
  options={{ label: "Driver Mappings" }}
/>,
    // 🔵 NEW – Driver Plan Manager (Fixed / Salary)
    <Resource
      key="driverPlanManager"
      name="driverPlanManager"
      list={DriverPlanManager}
      options={{ label: "🧮 Driver Plan Manager" }}
    />,
    <Resource
      key="driverAssignmentCostSetup"
      name="driverAssignmentCostSetup"
      list={DriverAssignmentCostSetup}
      options={{ label: "Driver Assignment Cost Setup" }}
    />,
    <Resource
      key="installmentAdjustments"
      name="installmentAdjustments"
      list={InstallmentAdjustments}
      options={{ label: "Monthly Installment Adjustments" }}
    />,
    <Resource
      key="installmentCompletionAlerts"
      name="installmentCompletionAlerts"
      list={InstallmentCompletionAlerts}
      options={{ label: "Installment Completion Alerts" }}
    />,


    <Resource
      key="accounts"
      name="accounts"
      list={AccountList}
      create={AccountCreate}
      edit={AccountEdit}
    />,

    <Resource
      key="transactions"
      name="transactions"
      list={TransactionList}
      create={TransactionCreate}
      edit={TransactionEdit}
      options={{ label: "Transactions" }}
    />,

    <Resource
      key="letters"
      name="letters"
      list={LetterList}
      create={LetterCreate}
      options={{ label: "Create Letter" }}
    />,

    <Resource
      key="pendingLetters"
      name="pendingLetters"
      list={MDLetterList}
      options={{ label: "Pending Letters" }}
    />,

    <Resource
      key="signedLetters"
      name="signedLetters"
      list={SignedDocuments}
      options={{ label: "Signed Letters" }}
    />,

    <Resource
      key="letterHeadUpload"
      name="letterHeadUpload"
      list={LetterHeadUpload}
      options={{ label: "Letterhead Upload" }}
    />,

    
    <Resource
  key="earningsImport"
  name="earningsImport"
  list={EarningsImport}
  options={{ label: "💰 Earnings Import" }}
/>,
<Resource
  key="dailyRideHailingUpload"
  name="dailyRideHailingUpload"
  list={DailyRideHailingUpload}
  options={{ label: "Daily Uber / Bolt Upload" }}
/>,
<Resource
  key="dailyBookingPortalUpload"
  name="dailyBookingPortalUpload"
  list={DailyBookingPortalUpload}
  options={{ label: "Daily Booking Portal Income Upload" }}
/>,
<Resource
  key="mdDailyIncomeReport"
  name="mdDailyIncomeReport"
  list={MDDailyIncomeReport}
  options={{ label: "Daily Income Report" }}
/>,
<Resource
  key="rideHailingUploadHistory"
  name="rideHailingUploadHistory"
  list={RideHailingUploadHistory}
  options={{ label: "Ride-Hailing Upload History" }}
/>,
<Resource key="rideHailingRangeReport" name="rideHailingRangeReport" list={RideHailingRangeReport} options={{ label: "Ride-Hailing Date Range Report" }} />,
<Resource
  key="rideHailingWeekPeriods"
  name="rideHailingWeekPeriods"
  list={RideHailingWeekPeriods}
  options={{ label: "Ride-Hailing Week Periods" }}
/>,
<Resource
  key="posUploadHistory"
  name="posUploadHistory"
  list={POSUploadHistory}
  options={{ label: "POS Upload History" }}
/>,
<Resource
  key="runningExpensesUpload"
  name="runningExpensesUpload"
  list={RunningExpensesUpload}
  options={{ label: "Running Expenses Upload" }}
/>,
<Resource
  key="posDeviceMappings"
  name="posDeviceMappings"
  list={POSMappingList}
  create={POSMappingCreate}
  edit={POSMappingEdit}
  options={{ label: "POS Device Mappings" }}
/>,


    <Resource
      key="trips_parking"
      name="trips_parking"
      list={ParkingUpdate}
      options={{ label: "🅿️ Parking Update" }}
    />,

    <Resource
      key="trips_pricing"
      name="trips_pricing"
      list={PricingUpdate}
      options={{ label: "💰 Pricing Update" }}
    />,
    <Resource
  key="invoiceGenerator"
  name="invoiceGenerator"
  list={InvoiceGenerator}
  options={{ label: "📄 Invoice Generator" }}
/>,

    <Resource
      key="client_pricing_setup"
      name="client_pricing_setup"
      list={ClientPricingSetup}
      options={{ label: "💼 Client Pricing Setup" }}
    />,
   
  ];

  /* -----------------------------------------------------
   MD Resources (Full access for visible menu items)
----------------------------------------------------- */
const mdResources = [
  /* Company Management */
  <Resource
    key="companies"
    name="companies"
    list={CompanyList}
    edit={CompanyEdit}
    options={{ label: "Companies" }}
  />,
  <Resource
    key="drivers"
    name="drivers"
    list={DriverList}
    edit={DriverEdit}
    options={{ label: "Employees" }}
  />,
  <Resource
    key="vehicles"
    name="vehicles"
    list={VehicleList}
    edit={VehicleEdit}
    options={{ label: "Vehicles" }}
  />,
  <Resource
    key="driverPlanManager"
    name="driverPlanManager"
    list={DriverPlanManager}
    options={{ label: "Driver Plan Manager" }}
  />,
  <Resource
    key="driverAssignmentCostSetup"
    name="driverAssignmentCostSetup"
    list={DriverAssignmentCostSetup}
    options={{ label: "Driver Assignment Cost Setup" }}
  />,
  <Resource
    key="installmentAdjustments"
    name="installmentAdjustments"
    list={InstallmentAdjustments}
    options={{ label: "Monthly Installment Adjustments" }}
  />,
  <Resource
    key="installmentCompletionAlerts"
    name="installmentCompletionAlerts"
    list={InstallmentCompletionAlerts}
    options={{ label: "Installment Completion Alerts" }}
  />,
  <Resource
  key="driverMappings"
  name="driverMappings"
  list={DriverMappingList}
  create={DriverMappingCreate}       // ✅ missing before
  edit={DriverMappingEdit}
  options={{ label: "Driver Mappings" }}
/>,

<Resource
  key="posDeviceMappings"
  name="posDeviceMappings"
  list={POSMappingList}
  create={POSMappingCreate}          // ✅ missing before
  edit={POSMappingEdit}
  options={{ label: "POS Device Mappings" }}
/>,


  /* Reports */
  <Resource key="ledger" name="ledger" list={LedgerList} />,
  <Resource
    key="carSheetCompanyPLExtractMd"
    name="carSheetCompanyPLExtract"
    list={CarSheetCompanyPLExtract}
    options={{ label: "Car Sheet P&L Extract" }}
  />,
  <Resource
    key="driversStatement"
    name="driversStatement"
    list={DriversStatement}
    options={{ label: "Drivers Statement" }}
  />,
  <Resource
    key="mdDailyIncomeReportMd"
    name="mdDailyIncomeReport"
    list={MDDailyIncomeReport}
    options={{ label: "Daily Income Report" }}
  />,
  <Resource
    key="upcomingExpenses"
    name="upcomingExpenses"
    list={UpcomingExpensesList}
    options={{ label: "Upcoming Expenses" }}
  />,

  /* Letters */
  <Resource
    key="letters"
    name="letters"
    list={LetterList}
    create={LetterCreate}
    options={{ label: "Create Letters" }}
  />,
  <Resource
    key="pendingLetters"
    name="pendingLetters"
    list={MDLetterList}
    options={{ label: "Pending Letters" }}
  />,
  <Resource
    key="signedLetters"
    name="signedLetters"
    list={SignedDocuments}
    options={{ label: "Signed Letters" }}
  />,
  <Resource
    key="mdSignature"
    name="mdSignature"
    list={MDSignatureUpload}
    options={{ label: "Signature & Stamp Upload" }}
  />,
];

    /* -----------------------------------------------------
     Operation Resources
  ----------------------------------------------------- */
  const operationResources = [
  <Resource
    key="drivers"
    name="drivers"
    list={DriverList}
    options={{ label: "Employees" }}
  />,
  <Resource
    key="driversStatement"
    name="driversStatement"
    list={DriversStatement}
    options={{ label: "Drivers Statement" }}
  />,
  <Resource
    key="mdDailyIncomeReportOperation"
    name="mdDailyIncomeReport"
    list={MDDailyIncomeReport}
    options={{ label: "Daily Income Report" }}
  />,
];




  const docsLinkResources = [
    <Resource
      key="drivers"
      name="drivers"
      list={DriverDocumentsList}
      options={{ label: "Driver Documents" }}
    />,
  ];

  /* -----------------------------------------------------
     Role-based logic
  ----------------------------------------------------- */
    let dashboardComponent = DashboardAdmin;
  let resourcesToRender = [...sharedResources, ...adminResources];

  if (role === "md") {
    dashboardComponent = DashboardMD;
    resourcesToRender = [...sharedResources, ...mdResources];
  } else if (role === "operation") {
    dashboardComponent = DashboardOperation;
    resourcesToRender = operationResources;
  } else if (role === "docs_link") {
    dashboardComponent = DashboardDocsLink;
    resourcesToRender = docsLinkResources;
  }


    return (
    <Admin
  authProvider={authProvider}
  dataProvider={dataProvider}
  dashboard={dashboardComponent}
  layout={MyLayout}     // 🔥 ESSENTIAL
  loginPage={CustomLoginPage}
    permissionsProvider={{
    getPermissions: async () =>
      (role || localStorage.getItem("role") || "guest").toLowerCase(),
  }}
>

      {resourcesToRender}
      
      <CustomRoutes>
    <Route path="/sign-letter/:id" element={<MDLetterSign />} />
    <Route path="/invoice-generator" element={<InvoiceGenerator />} />
    <Route path="/car-idle-report" element={<CarIdleReport />} />

    {(role === "admin" || role === "md") && (
      <>
        <Route path="/employee-docs-approvals" element={<EmployeeDocsApprovals />} />
        <Route
          path="/employee-docs-approvals/:token/:codeHash"
          element={<EmployeeDocsApprovalDetail />}
        />
      </>
    )}

    {(role === "admin" || role === "md" || role === "docs_link") && (
      <Route path="/driver-documents/:id" element={<DriverDocumentsEdit />} />
    )}

    
<Route path="/orphaned-income/:month" element={<OrphanedIncomeManager />} />

    {role === "admin" && (
        <Route path="/transactions-entry" element={<TransactionEntryPage />} />
    )}
</CustomRoutes>

    </Admin>
  );
}
